import { Link, useLocation, useNavigate } from '@remix-run/react'
import { useEffect, useMemo, useRef } from 'react'
import { useMediaQuery } from 'react-responsive'
import { type Currency, type User } from '@prisma/client'
import { Card } from '~/components/ui/card.tsx'
import { Spacer } from '~/components/ui/spacer.tsx'
import { ScrollArea } from '~/components/ui/scroll-area.tsx'
import { Button } from '~/components/ui/inputs/button/Button.tsx'
import { Icon } from '~/components/ui/icons/icon.tsx'
import { StarsRating } from '~/components/ui/stars.tsx'
import FAQ from '~/components/ui/faq.tsx'
import { Carousel } from '~/components/ui/carousel.tsx'
import { Breadcrumbs } from '~/components/ui/breadcrumbs.tsx'
import { ReviewCard } from '~/features/reviews/review-card.tsx'
import { VideoCard } from '~/components/cards/video-card.tsx'
import { getServicesStars } from '~/features/reviews/getStarsAndReviewDataForProfesional.tsx'
import { useConnectifContext } from '~/features/analytics/ConnectifProvider.tsx'
import { sendEventProductVisited } from '~/features/analytics/connectif-events.client.ts'
import { createGtagScript } from '~/features/analytics/gtags.client.ts'
import { useServiceBooking } from '~/features/schedules/useServiceBooking.ts'
import { ServiceBookingPanel } from '~/features/schedules/service-booking-panel.tsx'
import { getUserImgSrc } from '~/utils/misc.tsx'
import { Price } from '~/components/price.tsx'
import { dateToInput } from '~/utils/strings.ts'
import { scrollToReviewsSection } from './professional-profile.tsx'

type Props = {
  service: any
  currency: {
    name: Currency['name']
    value: Currency['value']
  }
  baseUrl: string
  stars: number
  latestReviews: any[]
  reviewsCount: number
  reviewers: Pick<User, 'id' | 'firstName'>[]
  similarServices: any[]
}

export function ServiceProfile({
  service,
  currency,
  baseUrl,
  stars,
  latestReviews,
  reviewsCount,
  reviewers,
  similarServices,
}: Props) {
  const { entityInfo } = useConnectifContext()
  const location = useLocation()
  const normalizedSearch = location.search === '?from=' ? '' : location.search
  const navigate = useNavigate()
  const fromRoute = new URLSearchParams(normalizedSearch).get('from') ?? ''

  const mapHomeRoute = (route: string) => {
    if (route === 'services') return '/servicios'
    if (route === 'professionals') return '/profesionales'
    if (route) return `/${route}`
    return '/'
  }

  useEffect(() => {
    let gtagScript: HTMLScriptElement | undefined
    if (typeof window !== 'undefined') {
      sendEventProductVisited(service as any, baseUrl, entityInfo)
      gtagScript = createGtagScript('fecha_hora_elegida_clic')
    }

    return () => {
      if (gtagScript) {
        document.head.removeChild(gtagScript)
      }
    }
  }, [baseUrl, entityInfo, service])

  const professionalVideos = useMemo(() => {
    return service.professional.video
      .filter((video: any) => !video.isPresentation)
      .sort(
        (a: any, b: any) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
  }, [service.professional.video])

  const reviewsAnchorId = 'service-reviews-section'
  const {
    disabledDates,
    selectedDate,
    setSelectedDate,
    selectedTime,
    setSelectedTime,
    availableHours,
    setFormatMonthAndYear,
    timeZone,
    setTimeZone,
  } = useServiceBooking({
    service,
  })

  const handleSubmit = (
    selectedDate: Date,
    selectedTime: string,
    timeZone: string,
  ) => {
    navigate(
      `/check/${service.id}?date=${dateToInput(
        selectedDate,
      )}&time=${selectedTime}&timeZone=${timeZone}`,
    )
  }

  const breadcrumbItems = [
    { label: 'Inicio', to: mapHomeRoute(fromRoute) },
    {
      label: service.professional.nickname ?? '',
      to: `/${service.professional.slug}${normalizedSearch}`,
    },
    {
      label: service.name,
      to: `/${service.professional.slug}/${service.slug}${normalizedSearch}`,
    },
  ]

  const wideBookingCalendar = useMediaQuery({ query: '(min-width: 1280px)' })
  const bookingGridRef = useRef<HTMLDivElement>(null)

  const bookingPanel = (
    <ServiceBookingPanel
      disabledDates={disabledDates}
      selectedDate={selectedDate}
      selectedTime={selectedTime}
      availableHours={availableHours}
      timeZone={timeZone}
      setFormatMonthAndYear={setFormatMonthAndYear}
      setSelectedDate={setSelectedDate}
      setSelectedTime={setSelectedTime}
      setTimeZone={setTimeZone}
      onSubmit={handleSubmit}
      submitLabel="Reservar"
      calendarVariant={wideBookingCalendar ? 'large' : 'default'}
    />
  )

  return (
    <div className="w-full min-w-0 overflow-x-clip pt-2">
      <Breadcrumbs
        items={breadcrumbItems}
        className="mb-1 flex items-center gap-2 text-base text-foreground sm:text-lg"
      />

      <Spacer size="4xs" />
      <div
        ref={bookingGridRef}
        className="grid min-w-0 gap-3 overflow-x-clip pb-24 sm:gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(18rem,0.88fr)] lg:items-start lg:gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(19rem,26rem)] xl:gap-5 2xl:grid-cols-[minmax(0,1fr)_minmax(21.25rem,28.75rem)] 2xl:gap-6"
      >
        <Card className="flex min-w-0 flex-col p-4 shadow-sm sm:p-5 lg:row-span-2 xl:p-6">
          <div className="mb-5 flex flex-wrap items-center gap-4">
            <div className="inline-flex rounded-[10px] border border-secondary-300 bg-secondary-200 px-4 py-2 text-sm font-semibold text-secondary">
              {service.category.name}
            </div>
            {reviewsCount > 0 && (
              <div className="flex items-center gap-1">
                <StarsRating value={stars} />
                <Link
                  to={`#${reviewsAnchorId}`}
                  className="text-sm text-foreground underline"
                  onClick={event => {
                    event.preventDefault()
                    scrollToReviewsSection(reviewsAnchorId)
                  }}
                >
                  {reviewsCount} valoraciones
                </Link>
              </div>
            )}
          </div>
          <h1 className="break-words text-h4 sm:text-h3">{service.name}</h1>
          <Spacer size="4xs" />
          <ScrollArea className="max-h-[180px] overflow-auto pr-2 text-foreground lg:max-h-[200px] xl:max-h-[230px]">
            <p className="whitespace-pre-line break-words text-sm sm:text-base">
              {service.description}
            </p>
          </ScrollArea>

          <hr className="my-9 border-input" />

          <div className="flex min-w-0 flex-col gap-5 sm:flex-row sm:flex-wrap sm:items-center">
            <img
              className="mx-auto h-[68px] w-[68px] rounded-full object-cover sm:mx-0"
              alt={`${service.professional.nickname}`}
              src={getUserImgSrc(service.professional.avatarImage?.id)}
            />

            <div className="flex flex-col text-center sm:text-left">
              <p className="text-h5 text-secondary">
                con {service.professional.nickname}
              </p>
              <Link
                to={`/${service.professional.slug}${normalizedSearch}`}
                className="text-sm text-foreground underline"
              >
                Ver perfil
              </Link>
            </div>
            <div
              className={`grid w-full min-w-0 grid-cols-2 gap-3 text-secondary sm:ml-auto sm:w-auto sm:gap-4 ${
                service.isVideoCall ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
              }`}
            >
              <div className="flex flex-col items-center text-center">
                <Icon name="credit-card" size="drawer" className="mb-2" />
                <Price
                  className="break-words p-0 text-xs font-semibold leading-tight text-secondary sm:text-sm"
                  amount={service.price * currency.value}
                  currency={currency.name}
                  rounded={true}
                  isSessionValue={true}
                />
              </div>
              <div className="flex flex-col items-center text-center">
                <Icon
                  name={service.isVideoCall ? 'video' : 'file-text'}
                  size="drawer"
                  className="mb-2"
                />
                <span className="break-words text-xs font-semibold leading-tight sm:text-sm">
                  {service.isVideoCall ? 'Videollamada' : 'Documento/Audio'}
                </span>
              </div>
              {service.isVideoCall && (
                <div className="col-span-2 flex flex-col items-center text-center sm:col-span-1">
                  <Icon name="clock" size="drawer" className="mb-2" />
                  <span className="break-words text-xs font-semibold leading-tight sm:text-sm">
                    {service.duration} min
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 border-t border-input pt-6 lg:hidden">
            <Card className="p-4 shadow-sm sm:p-5">{bookingPanel}</Card>
          </div>

          <div className="mt-8 border-t border-input pt-8">
            {latestReviews.length > 0 && (
              <>
                <section id={reviewsAnchorId}>
                  <div className="mb-7 flex flex-wrap items-center gap-3 sm:gap-4">
                    <h3 className="text-h5">Mis valoraciones</h3>
                    {reviewsCount > 0 && (
                      <div className="flex items-center gap-1.5">
                        <StarsRating value={stars} />
                        <span className="text-sm text-foreground">
                          ({reviewsCount} valoraciones)
                        </span>
                      </div>
                    )}
                  </div>
                  <Spacer size="5xs" />
                  <Carousel
                    slides={latestReviews.map(review => (
                      <ReviewCard
                        key={review.id}
                        review={review as any}
                        user={
                          reviewers.find(
                            reviewer => reviewer.id === review.reviewerId,
                          ) as any
                        }
                      />
                    ))}
                    slidesPerView={1.1}
                    spaceBetween={16}
                    withDesktopNavigation
                    loop={true}
                    breakpoints={{
                      380: {
                        slidesPerView: 1.4,
                      },
                      768: {
                        slidesPerView: 2.2,
                      },
                      960: {
                        slidesPerView: 2.6,
                      },
                      1400: {
                        slidesPerView: 3.2,
                      },
                    }}
                  />
                </section>
              </>
            )}

            {professionalVideos.length > 0 && (
              <>
                <Spacer size="3xs" />
                <section id="service-videos-section">
                  <h3 className="mb-7 text-h5">Mis videos</h3>
                  <Spacer size="5xs" />
                  <Carousel
                    slides={professionalVideos.map((video: any) => (
                      <VideoCard key={video.id} video={video as any} />
                    ))}
                    slidesPerView={1.1}
                    spaceBetween={16}
                    loop={true}
                    breakpoints={{
                      380: {
                        slidesPerView: 1.4,
                      },
                      768: {
                        slidesPerView: 2.2,
                      },
                      960: {
                        slidesPerView: 2.6,
                      },
                      1400: {
                        slidesPerView: 3.2,
                      },
                    }}
                  />
                </section>
              </>
            )}

            <Spacer size="3xs" />
            <section id="service-faq-section">
              <h3 className="text-h5">Preguntas frecuentes</h3>
              <Spacer size="5xs" />
              <div className="max-w-[760px]">
                <FAQ />
              </div>
            </section>

            {similarServices.length > 0 && (
              <>
                <Spacer size="3xs" />
                <section id="similar-services-section">
                  <h3 className="mb-7 text-h5">Servicios similares</h3>
                  <Spacer size="5xs" />
                  <Carousel
                    slides={similarServices.map(similarService => {
                      const serviceName = similarService.name ?? 'Servicio'
                      const serviceDescription =
                        similarService.description ?? ''
                      const professionalName =
                        similarService.professional?.nickname ?? 'Profesional'
                      const similarStars = getServicesStars(
                        similarService.professional?.services ?? [],
                      )

                      return (
                        <div
                          key={similarService.id}
                          className="flex w-full min-w-0 flex-col gap-3 rounded-lg border border-input bg-white p-4 sm:flex-row sm:items-center sm:gap-4 sm:p-5"
                        >
                          <div className="flex min-w-[110px] flex-col items-center gap-2 sm:min-w-[140px] sm:gap-3">
                            <Link
                              to={`/${similarService.professional.slug}${normalizedSearch}`}
                              className="flex flex-col items-center gap-2 sm:gap-3"
                            >
                              <img
                                className="h-[64px] w-[64px] rounded-full object-cover sm:h-[82px] sm:w-[82px]"
                                alt={professionalName}
                                src={getUserImgSrc(
                                  similarService.professional?.avatarImage?.id,
                                )}
                              />
                              <StarsRating value={similarStars} />
                              <p className="text-center text-sm font-bold text-secondary sm:text-base">
                                con {professionalName}
                              </p>
                            </Link>

                            <Link
                              to={`/${similarService.professional.slug}/${similarService.slug}${normalizedSearch}`}
                            >
                              <Button size={'sm'} className="w-full px-3">
                                <Icon
                                  name="calendar"
                                  size="font"
                                  className="mr-2 shrink-0 text-white"
                                />
                                <span className="text-[10px] sm:text-xs">
                                  Haz tu reserva
                                </span>
                              </Button>
                            </Link>
                          </div>

                          <div className="min-w-0 flex-1">
                            <h4 className="break-words text-h5 leading-tight sm:text-h4">
                              {serviceName}
                            </h4>
                            <Spacer size="5xs" />
                            <ScrollArea className="h-[150px] overflow-auto pr-2 sm:h-[170px]">
                              <p className="whitespace-pre-line break-words text-sm text-foreground-muted sm:text-base">
                                {serviceDescription}
                              </p>
                            </ScrollArea>
                          </div>
                        </div>
                      )
                    })}
                    slidesPerView={1.2}
                    spaceBetween={20}
                    loop={true}
                    breakpoints={{
                      960: {
                        slidesPerView: 1.3,
                      },
                      1280: {
                        slidesPerView: 1.8,
                      },
                    }}
                  />
                </section>
              </>
            )}
          </div>
        </Card>

        <Card className="hidden w-full min-w-0 max-w-[450px] justify-self-center p-4 shadow-sm sm:p-5 lg:sticky lg:top-16 lg:row-span-2 lg:block lg:w-full lg:max-w-none lg:self-start lg:justify-self-end lg:p-4 xl:p-5 2xl:p-6">
          <ServiceBookingPanel
            disabledDates={disabledDates}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            availableHours={availableHours}
            timeZone={timeZone}
            setFormatMonthAndYear={setFormatMonthAndYear}
            setSelectedDate={setSelectedDate}
            setSelectedTime={setSelectedTime}
            setTimeZone={setTimeZone}
            onSubmit={handleSubmit}
            submitLabel="Reservar"
            calendarVariant={wideBookingCalendar ? 'large' : 'default'}
            scrollContainerRef={bookingGridRef}
          />
        </Card>
      </div>
    </div>
  )
}
